import { useState, useEffect } from "react";

import { useTranslation } from "@impero-com/common/utils/i18n";
import { useNavigate, useLocation } from "react-router-dom";

import { OrgApplicationBar } from "../../../components/applicationBar/orgApplicationBar";

import { BulkAssignmentStepsContainer, BulkAssignStep } from "./stepsContainer";

const STEP_PARAM = "step";
const ALLOWED_URL_STEPS: BulkAssignStep[] = ["export", "upload"];

function parseBulkAssignStepFromUrlParam(value: string | null): BulkAssignStep {
    switch (value) {
        case "export":
        case "upload":
        case "confirm":
            return value;
        default:
            return "export";
    }
}

export function ControlsBulkAssignPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const [currentStep, setCurrentStep] = useState<BulkAssignStep>("export");
    const [hasError, setHasError] = useState(false);

    const getStepStatus = (stepId: BulkAssignStep): "error" | undefined => {
        return currentStep === stepId && hasError ? "error" : undefined;
    };

    const handleStepChange = (step: BulkAssignStep) => {
        setCurrentStep(step);

        // Only update URL for export and upload steps
        if (ALLOWED_URL_STEPS.includes(step)) {
            const params = new URLSearchParams(location.search);
            params.set(STEP_PARAM, step);
            navigate({ search: params.toString() }, { replace: true });
        }
    };

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const stepParam = params.get(STEP_PARAM);
        const parsedStep = parseBulkAssignStepFromUrlParam(stepParam);

        if (parsedStep && ALLOWED_URL_STEPS.includes(parsedStep)) {
            // eslint-disable-next-line react-hooks/set-state-in-effect
            setCurrentStep(parsedStep);
        }
    }, [location.search]);

    return (
        <OrgApplicationBar
            title={{
                title: t("ControlsBulkAssign_Title"),
                breadCrumbs: [
                    {
                        label: t("ControlManagement_ModuleTitle"),
                        url: "/controls",
                    },
                ],
            }}
            navigation={{
                type: "Steps",
                currentStepId: currentStep,
                stepSpecs: [
                    {
                        id: "export",
                        label: t("ControlsBulkAssign_Step1_Title"),
                        status: getStepStatus("export"),
                        onClick: () => handleStepChange("export"),
                    },
                    {
                        id: "upload",
                        label: t("ControlsBulkAssign_Step2_Title"),
                        status: getStepStatus("upload"),
                        onClick: () => handleStepChange("upload"),
                    },
                    {
                        id: "confirm",
                        label: t("ControlsBulkAssign_Step3_Title"),
                        status: getStepStatus("confirm"),
                        onClick: () => {},
                    },
                ],
            }}
        >
            <BulkAssignmentStepsContainer
                currentStep={currentStep}
                onStepChange={handleStepChange}
                onErrorChange={setHasError}
            />
        </OrgApplicationBar>
    );
}
